/**
 * When the app goes to the background and comes back.
 *
 * This is the highest-consequence adapter after storage, and the reason is in
 * `ROADMAP.md`: "an IndexedDB transaction aborts when iOS Safari backgrounds
 * the tab mid-write... precisely the thing that eats a scorebook in the third
 * quarter."
 *
 * A volunteer takes a phone call between possessions. The tab backgrounds. The
 * scorebook has to know that happened so it can finish or abandon a write
 * deliberately rather than be interrupted halfway.
 *
 * `visibilitychange` on the web; native lifecycle events under Capacitor, which
 * fire in cases a webview never sees.
 */

import { browserDocument, type Adapter } from './host.js';

export type AppState = 'active' | 'background';

export interface AppLifecycle extends Adapter {
  readonly current: () => AppState;
  /** Returns an unsubscribe function. */
  readonly onChange: (listener: (state: AppState) => void) => () => void;
}

export const appState: AppLifecycle = {
  // `visibilityState` is the capability. A document without it cannot say
  // whether the app is backgrounded, which is the only question asked here.
  isSupported: () => typeof browserDocument()?.visibilityState === 'string',

  current: () => {
    const doc = browserDocument();
    // Active when unknown. Nothing should stop working because the host does
    // not report visibility.
    return doc === null || doc.visibilityState === 'visible' ? 'active' : 'background';
  },

  onChange: (listener) => {
    const doc = browserDocument();
    if (doc === null) return () => {};

    const handler = () => {
      listener(doc.visibilityState === 'visible' ? 'active' : 'background');
    };
    doc.addEventListener('visibilitychange', handler);
    return () => {
      doc.removeEventListener('visibilitychange', handler);
    };
  },
};
